import { memo, useMemo } from 'react';

import { TranscriptionSegment } from '@/services/types/transcript-types';

import { TranscriptSegment as Row } from './TranscriptSegment';

interface TranscriptListProps {
  segments: TranscriptionSegment[];
  searchQuery: string;
  matchIds: string[];
  matchRanges: Record<string, Array<[number, number]>>;
  playingSegmentId: string | null;
  audioIsPlaying: boolean;
  onSegmentInteract: (id: string, ev?: React.KeyboardEvent) => void;
  segmentRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
}

export const TranscriptList = memo(
  ({
    segments,
    searchQuery,
    matchIds,
    matchRanges,
    playingSegmentId,
    audioIsPlaying,
    onSegmentInteract,
    segmentRefs,
  }: TranscriptListProps) => {
    /* ---------------- Speaker colour helpers ---------------- */
    const speakers = useMemo(
      () =>
        Array.from(
          new Set(segments.map((s) => s.external_speaker_label || 'Speaker')),
        ),
      [segments],
    );

    const getSpeakerColor = (speaker: string) => {
      if (speakers.length <= 1) return 'bg-blue-50';
      const index = speakers.indexOf(speaker);
      return index % 2 === 0 ? 'bg-blue-50' : 'bg-violet-50';
    };
    const getSpeakerTextColor = (speaker: string) => {
      if (speakers.length <= 1) return 'text-blue-600';
      const index = speakers.indexOf(speaker);
      return index % 2 === 0 ? 'text-blue-600' : 'text-violet-600';
    };

    /* ---------------- Highlight helper ---------------- */
    const highlightText = (segmentId: string, text: string, query: string) => {
      if (!query.trim()) return text;
      try {
        if (matchRanges[segmentId]) {
          // Build highlighted HTML using ranges from fuse
          let result = '';
          let lastIndex = 0;
          matchRanges[segmentId].forEach(([start, end]) => {
            result += text.slice(lastIndex, start);
            result += `<mark class="bg-yellow-100 text-yellow-900 rounded px-1 font-medium">${text.slice(
              start,
              end + 1,
            )}</mark>`;
            lastIndex = end + 1;
          });
          result += text.slice(lastIndex);
          return result;
        }
        // fallback to simple regex
        const regex = new RegExp(
          `(${query.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')})`,
          'gi',
        );
        return text.replace(
          regex,
          '<mark class="bg-yellow-100 text-yellow-900 rounded px-1 font-medium">$1</mark>',
        );
      } catch {
        return text;
      }
    };

    const formatTime = (seconds: number) => {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60)
        .toString()
        .padStart(2, '0');
      return `${m}:${s}`;
    };

    return (
      <div className="py-6 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
        <div className="space-y-4">
          {segments.map((segment) => {
            const speaker = segment.external_speaker_label || 'Speaker';
            const isPlaying = segment.id === playingSegmentId;
            const isMatch = matchIds.includes(segment.id);
            return (
              <div
                key={segment.id}
                ref={(el) => {
                  segmentRefs.current[segment.id] = el;
                }}
              >
                <Row
                  segment={segment}
                  isPlaying={isPlaying}
                  audioIsPlaying={audioIsPlaying}
                  isMatch={isMatch}
                  speakerBgColor={getSpeakerColor(speaker)}
                  speakerTextColor={getSpeakerTextColor(speaker)}
                  formattedStartTime={formatTime(segment.start_time)}
                  onInteract={onSegmentInteract}
                  highlightText={(t, q) => highlightText(segment.id, t, q)}
                  searchQuery={searchQuery}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

TranscriptList.displayName = 'TranscriptList';
